// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {Tooltip} from '@deere/isg.component-library';
import {IconEdit, IconTerminal} from '@deere/icons';
import OnlinkButton from 'Ui/components/common/onlink-button';
import {joinClassNames} from 'Utils/html-utils';
import {formatNumberOrDefault, formatStatus, formatTimeOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {formatModelName} from 'Ui/models/equipment-model';
import equipmentStatusConstants from 'Ui/constants/equipment-status-constants';
import {openDialog as openReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import MediaQuery, {MOBILE_MEDIA_QUERY} from 'Ui/components/higher-order-components/media-query';

function Header(props) {
    const {
        equipment,
        isMobile,
        openDialog,
        servicePriority,
        translations,
        updateEquipment,
        canEditEquipmentList,
        isFleetToMaintenanceEnabled
    } = props;

    if (!equipment) {
        return null;
    }

    const {
        equipmentName,
        lastReportedMtgId,
        manufacturerName,
        serialNumber,
        status,
        totalHours,
        timeLastMeterReading
    } = equipment;

    const modelDescription = formatModelName(equipment);
    const statusConstant = equipmentStatusConstants[status] || {};
    const formattedStatus = formatStatus(equipment, translations);
    const formattedLastMeterReading = formatTimeOrDefault(timeLastMeterReading);
    const formattedTotalHours = formatNumberOrDefault(totalHours, 1);

    const btnWrapperStyle = isMobile ? {
        position: 'absolute',
        bottom: '32px'
    } : null;

    return (
        <div className='settings-body'>
            <div className='settings-group horizontal'>
                <span className='medium-section-title'>
                    {equipmentName}
                </span>
                {
                    canEditEquipmentList && !isFleetToMaintenanceEnabled &&
                    <div
                        className='edit-button'
                        style={btnWrapperStyle}
                    >
                        <OnlinkButton
                            className='primary'
                            onClick={() => {
                                openDialog({
                                    equipment,
                                    servicePriority,
                                    updateFleetEquipment: updateEquipment
                                });
                            }}
                            startIcon={isMobile ? null : <IconEdit/>}
                            variant={isMobile ? 'borderless' : 'primary'}
                        >
                            {translations.EDIT}
                        </OnlinkButton>
                    </div>
                }
            </div>
            <div className='settings-group mobile'>
                <span className='body-text-section-title'>
                    {`${manufacturerName} ${modelDescription}`}
                </span>
                <span className='small-body-text last-item'>
                    {`${formattedTotalHours} ${translations.HOURS.toUpperCase()}`}
                </span>
            </div>
            <div className='settings-group mobile'>
                <span className='small-body-text'>
                    {serialNumber}
                </span>
                <div className={'small-body-text last-item'}>
                    {`${translations.UPDATED}: ${formattedLastMeterReading}`}
                </div>
            </div>
            <div className='settings-group mobile'>
                {
                    lastReportedMtgId &&
                    <div className='terminal-id'>
                        <Tooltip
                            placement='bottom'
                            title={translations.ONLINK_TELEMATICALLY_ENABLED}
                        >
                            <div>
                                <IconTerminal/>
                            </div>
                        </Tooltip>
                        <span className='small-body-text telematically-enabled-text'>
                            {`${translations.TERMINAL_ID.toUpperCase()}: ${lastReportedMtgId}`}
                        </span>
                    </div>
                }
                <span
                    className={joinClassNames(
                        'small-body-text equipment-status last-item',
                        statusConstant.color
                    )}
                >
                    {formattedStatus}
                </span>
            </div>
        </div>
    );
}

Header.propTypes = {
    canEditEquipmentList: PropTypes.bool,
    equipment: PropTypes.equipment,
    isFleetToMaintenanceEnabled: PropTypes.bool,
    isMobile: PropTypes.bool,
    openDialog: PropTypes.func,
    servicePriority: PropTypes.array,
    translations: PropTypes.translations,
    updateEquipment: PropTypes.func
};

export function mapDispatchToProps(dispatch) {
    return {
        openDialog(props) {
            dispatch(openReduxDialog(dialogTypes.EQUIPMENT_EDIT_DIALOG, props));
        }
    };
}

export default connect(null, mapDispatchToProps)(MediaQuery(MOBILE_MEDIA_QUERY)(Header));
